import axios, { AxiosResponse } from "axios";
import moment from "moment";
import { NotifyManager } from "notify-component";

import type {
    ChatAxiosResponse,
    ChatErrorCallback,
    ChatSuccessCallback,
} from "./types";
import {
    API_URL,
    getCommonHeaders,
    getUserId,
    SendMessageInterface,
} from "./utils/utils";

export const getChats = (
    userId: string | number | null,
    responseCallback: (response: ChatAxiosResponse) => void,
    errorCallback?: ChatErrorCallback
) => {
    axios
        .get(`${API_URL}/api/chats`, {
            headers: getCommonHeaders(
                localStorage.getItem("api_token") as string,
                getUserId(userId)
            ),
        })
        .then((response) => responseCallback && responseCallback(response))
        .catch((error) => {
            console.log(error);
            if (errorCallback) {
                errorCallback(error);
            } else {
                const status = error.response?.status;
                const message =
                    error.response?.data?.meta?.text ||
                    error.message ||
                    "Ошибка загрузки чатов";
                if (status >= 500) {
                    NotifyManager.error(
                        "Ошибка сервера",
                        "Произошла ошибка на сервере. Попробуйте позже."
                    );
                } else {
                    NotifyManager.error("Ошибка", message);
                }
            }
        });
};

export const getRecentUnassignedChats = (
    userId: string | number | null,
    responseCallback: (response: ChatAxiosResponse) => void,
    errorCallback?: ChatErrorCallback
) => {
    axios
        .get(`${API_URL}/api/chats/support`, {
            headers: getCommonHeaders(
                localStorage.getItem("api_token") as string,
                getUserId(userId)
            ),
        })
        .then((response) => responseCallback && responseCallback(response))
        .catch((error) => {
            console.log(error);
            if (errorCallback) {
                errorCallback(error);
            }
        });
};

/** id чата из ответа `POST /api/chats` (после успешного `result === "success"`). */
export function extractChatIdFromCreateChatResponse(
    response: AxiosResponse<{ data?: { id?: string | number } }>
): string | null {
    const id = response?.data?.data?.id;
    if (id == null || id === "") return null;
    return String(id);
}

export type CreateChatOptions = {
    /** CRM WhatsApp: не вызывать POST /api/chats/:id/support — иначе 403 */
    skipSupportAssign?: boolean;
};

export const createChat = (
    userId: string | number | null,
    receiverId: string | number,
    responseCallback: (response: ChatAxiosResponse) => void,
    errorCallback?: ChatErrorCallback,
    options?: CreateChatOptions
) => {
    axios
        .post(
            `${API_URL}/api/chats`,
            { user_id: receiverId },
            {
                headers: getCommonHeaders(
                    localStorage.getItem("api_token") as string,
                    getUserId(userId)
                ),
            }
        )
        .then((response) => {
            const { data } = response;
            if (data?.result && data?.data && data.result === "success") {
                const { chat_exists } = data.data;
                const finish = () => responseCallback && responseCallback(response);
                if (chat_exists && !options?.skipSupportAssign) {
                    assignSupportRequest(data.data.id, userId, finish, finish);
                } else {
                    finish();
                }
            } else {
                const err = new Error(
                    (data as { meta?: { text?: string } })?.meta?.text ??
                        "Не удалось создать чат"
                );
                if (errorCallback) errorCallback(err);
                else console.log("createChat: unexpected response", data);
            }
        })
        .catch((error) => {
            console.log(error);
            if (errorCallback) errorCallback(error);
        });
};

export const getMessages = (
    userId: string | number | null,
    chatId: string | null | number,
    responseCallback: (response: ChatAxiosResponse) => void,
    additionalParams: Record<string, any> | null = null,
    errorCallback?: ChatErrorCallback
) => {
    axios
        .post(
            `${API_URL}/api/trigger/messages`,
            { chat_id: chatId, take: 999, ...(additionalParams ?? {}) },
            {
                headers: getCommonHeaders(
                    localStorage.getItem("api_token") as string,
                    getUserId(userId)
                ),
            }
        )
        .then((response) => {
            responseCallback && responseCallback(response);
        })
        .catch((error) => {
            console.log("Ошибка:", error.response?.data || error.message);
            if (errorCallback) {
                errorCallback(error);
            }
        });
};

export const postMessage = (
    requestParams: SendMessageInterface,
    responseCallback: (response: ChatAxiosResponse) => void,
    errorCallback?: ChatErrorCallback
) => {
    const {
        user_id,
        chat_id,
        sender_id,
        text,
        attachment_file,
        type,
        chat_type,
        status,
        timestamp,
    } =
        requestParams;
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        user_id
    );

    axios
        .post(
            `${API_URL}/api/trigger/message`,
            {
                chat_id,
                sender_id,
                text,
                attachment_file,
                type,
                chat_type,
                status,
                timestamp,
            },
            { headers: headers }
        )
        .then((response) => {
            const data = response?.data as
                | { result?: string; meta?: { text?: string }; message?: string }
                | undefined;
            if (data?.result === "error" || data?.result === "false") {
                const err = new Error(
                    data?.meta?.text ?? data?.message ?? "Ошибка отправки сообщения"
                );
                if (errorCallback) errorCallback(err);
                return;
            }
            responseCallback && responseCallback(response);
        })
        .catch((error) => {
            console.log("Ошибка:", error.response?.data || error.message);
            if (errorCallback) {
                errorCallback(error);
            }
        });
};

export const setRead = (chat_id: string | number, userId: string | number | null, responseCallback: ChatSuccessCallback) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .post(
            `${API_URL}/api/trigger/set-read`,
            { chat_id: chat_id },
            { headers }
        )
        .then((response: ChatAxiosResponse) => responseCallback && responseCallback(response))
        .catch((error) => console.log(error));
};

export const imageFilePath = (chatId: string | number, fileName: string) =>
    `${API_URL}/api/chats/file/${chatId as string}/${fileName}`;

export const getImage = (
    chatId: string | number,
    fileName: string,
    userId: string | number | null,
    responseCallback: ChatSuccessCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .post(
            `${API_URL}/api/chats/get-file`,
            {
                chat_id: chatId,
                filename: fileName,
            },
            { headers: headers }
        )
        .then((response) => {
            responseCallback && responseCallback(response);
        });
};

export const getImageUrl = (
    chatId?: string | number | undefined,
    fileName?: string,
    userId?: string | number
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const headers = getCommonHeaders(
            localStorage.getItem("api_token") as string,
            getUserId(userId)
        );

        axios
            .post(
                `${API_URL}/api/chats/get-file`,
                { chat_id: chatId, filename: fileName },
                { headers: headers, responseType: "blob" }
            )
            .then((response) => {
                const blob = new Blob([response.data], {
                    type: response.data.type,
                });
                const imageUrl = URL.createObjectURL(blob);
                resolve(imageUrl);
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const assignSupportRequest = (
    chatId: string | number,
    userId: string | number | null,
    responseCallback: ChatSuccessCallback,
    /** Если задан — вызывается вместо только NotifyManager при ошибке (например, всё равно продолжить сценарий). */
    errorCallback?: ChatErrorCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .post(`${API_URL}/api/chats/${chatId}/support`, {}, { headers })
        .then((response) => {
            responseCallback && responseCallback(response);
        })
        .catch((error) => {
            console.log(error);
            if (errorCallback) {
                errorCallback(error);
            } else {
                const errorMessage =
                    error?.response?.data?.meta?.text ??
                    "Ошибка привязки агента поддержки к чату";

                NotifyManager.error("Ошибка", errorMessage);
            }
        });
};

export const unassignSupportRequest = (
    chatId: string | number,
    userId: string | number | null,
    responseCallback: ChatSuccessCallback,
    errorCallback?: ChatErrorCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .delete(`${API_URL}/api/chats/${chatId}/support`, { headers })
        .then((response) => {
            responseCallback && responseCallback(response);
        })
        .catch((error) => {
            console.log(error);
            if (errorCallback) {
                errorCallback(error);
            } else {
                const errorMessage =
                    error?.response?.data?.meta?.text ??
                    "Ошибка отвязки агента поддержки от чата";
                NotifyManager.error("Ошибка", errorMessage);
            }
        });
};

export const deleteChatRequest = (
    chatId: string | number,
    userId: string | number | null,
    responseCallback?: (response: ChatAxiosResponse) => void,
    errorCallback?: ChatErrorCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .delete(`${API_URL}/api/chats/${chatId}`, { headers })
        .then((response) => {
            responseCallback?.(response);
        })
        .catch((error) => {
            console.log(error);
            if (errorCallback) {
                errorCallback(error);
            } else {
                const errorMessage =
                    error?.response?.data?.meta?.text ?? "Ошибка удаления чата";
                NotifyManager.error("Ошибка", errorMessage);
            }
        });
};

export const createCohortRequest = (
    title: string,
    userId: string | number | null,
    receiversList: number[],
    responseCallback: (response: ChatAxiosResponse) => void
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .post(
            `${API_URL}/api/contacts/lists`,
            { user_ids: receiversList, title },
            { headers }
        )
        .then((response) => {
            responseCallback?.(response);
        })
        .catch((error) => {
            const errorMessage =
                error.response?.data?.meta?.text ??
                error.response?.data ??
                error.message ??
                "Произошла неизвестная ошибка";

            NotifyManager.error("Ошибка", errorMessage);
        });
};

export const getCohorts = (
    userId: string | number | null,
    responseCallback: ChatSuccessCallback,
    errorCallback: ChatErrorCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .get(`${API_URL}/api/contacts/lists`, { headers })
        .then((response) => responseCallback && responseCallback(response))
        .catch((error) => errorCallback && errorCallback(error));
};

export const getMailingsRequest = (
    userId: string | number | null,
    responseCallback: (data: any) => void,
    errorCallback?: ChatErrorCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );
    axios
        .get(`${API_URL}/api/templates`, { headers })
        .then((response) => {
            if (response.data?.result === "success") {
                responseCallback && responseCallback(response.data.data || []);
            }
        })
        .catch((error) => {
            console.error("Ошибка загрузки рассылок:", error);
            if (errorCallback) {
                errorCallback(error);
            } else {
                responseCallback && responseCallback([]);
            }
        });
};

export const sendMailings = (
    mailingsList: {
        templates?: Array<string | number> | null;
        text?: string | null;
        title?: string | null;
        type: "push" | "mail";
        contact_list_id?: number | null;
        start_time: string | Date | null;
        user_ids?: number[];
    },
    userId: string | number | null,
    responseCallback?: (response: ChatAxiosResponse) => void,
    errorCallback?: (message: string) => void
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );
    const parsedTime = moment(mailingsList.start_time);
    const formattedStartTime = parsedTime.isValid()
        ? parsedTime.format("YYYY-MM-DD HH:mm")
        : null;

    const handleRequest = (payload: Record<string, any>) =>
        axios
            .post(`${API_URL}/api/marketer/mailings`, payload, { headers })
            .then((response) => {
                responseCallback?.(response);
            })
            .catch((error) => {
                errorCallback?.(
                    `Ошибка отправки рассылки (${mailingsList.type}): ${
                        error?.response?.data?.meta?.text ?? error
                    }`
                );
            });

    if (mailingsList.type === "push") {
        handleRequest({
            start_time: formattedStartTime,
            contact_list_id: mailingsList.contact_list_id,
            type: "push",
            app_name: "whiteswan",
            data: {
                push: {
                    title: mailingsList?.title ?? "Входящее уведомление",
                    text: mailingsList.text,
                },
            },
        });
    } else {
        mailingsList.templates?.forEach((templateId) =>
            handleRequest({
                template_id: templateId,
                start_time: formattedStartTime,
                contact_list_id: mailingsList.contact_list_id,
                type: "mail",
            })
        );
    }
};

export const getMailings = (
    userId: string | number | null,
    callbackOnSuccess: (response: ChatAxiosResponse) => void,
    errorCallback?: ChatErrorCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    return axios
        .get(`${API_URL}/api/marketer/mailings`, { headers })
        .then((response) => callbackOnSuccess && callbackOnSuccess(response))
        .catch((error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        });
};

export const exportContacts = (
    listId: number,
    userId: string | number | null,
    responseCallback?: (response: ChatAxiosResponse) => void,
    errorCallback?: ChatErrorCallback
) => {
    const headers = getCommonHeaders(
        localStorage.getItem("api_token") as string,
        getUserId(userId)
    );

    axios
        .post(`${API_URL}/api/contacts/export`, { list_id: listId }, { headers })
        .then((response) => {
            responseCallback?.(response);
        })
        .catch((error) => {
            console.error("Ошибка экспорта контактов:", error);
            errorCallback?.(error);
        });
};

